import { useSetAtom } from 'jotai'
import React from 'react'
import { useAsyncFn, useMountedState } from 'react-use'

import { graphql } from '~/gql'
import { type BasicOutputGenerationPage_GenerateBasicOutputsStatusQuery } from '~/gql/graphql'
import { useGraphQLRequest } from '~/hooks/useGraphQLRequest'
import { exists } from '~/utils'

import {
  awaitIdAtom,
  type CustomRoleAxes,
  expandCustomerAttributeGroupId,
  type FilterPatternDraft,
  phaseAtom,
  type QuestionAssignments,
} from '../atoms'

const generateBasicOutputsMutationDocument = graphql(`
  mutation BasicOutputGenerationPage_GenerateBasicOutputs(
    $surveyId: String!
    $brandGroupId: Int!
    $brandIds: [Int!]
    $isEntireBrandGroupSelected: Boolean!
    $questionSelections: [QuestionSelectionInput!]!
    $filterPatterns: [FilterPatternInput!]!
  ) {
    generateBasicOutputs(
      surveyId: $surveyId
      brandGroupId: $brandGroupId
      brandIds: $brandIds
      isEntireBrandGroupSelected: $isEntireBrandGroupSelected
      questionSelections: $questionSelections
      filterPatterns: $filterPatterns
    ) {
      awaitId
    }
  }
`)

const generateBasicOutputsStatusQueryDocument = graphql(`
  query BasicOutputGenerationPage_GenerateBasicOutputsStatus(
    $awaitId: String!
  ) {
    generateBasicOutputsStatus(awaitId: $awaitId) {
      arrived
      error
      progress {
        completed
        total
      }
      result {
        filterResults {
          filterName
          sampleSize
          brandResults {
            brandId
            brandName
            outputs {
              __typename
              outputType
              segmentMode
              ... on Brands9segsInBrandGroupOutputResult {
                result {
                  result {
                    id
                    name
                    number
                    result {
                      count
                      segmentNumber
                    }
                  }
                }
              }
              ... on AnswersByMultiQuestionsOutputResult {
                result {
                  groupCounts
                  results {
                    questionId
                    answers {
                      answerOption {
                        option
                        text
                      }
                      count
                    }
                    groupAnswers {
                      group
                      answers {
                        answerOption {
                          option
                          text
                        }
                        count
                      }
                    }
                  }
                }
              }
              ... on CustomerAttributeAxisOutputResult {
                result {
                  customerAttributes {
                    byCustomerAttributeId
                    result {
                      totalCount
                      brands {
                        brandGroupId
                        brandNumber
                        result {
                          count
                          segmentNumber
                        }
                      }
                    }
                  }
                }
              }
              ... on CustomizedAnalysisOutputResult {
                result {
                  brands9segs {
                    id
                    name
                    number
                    result {
                      count
                      segmentNumber
                    }
                  }
                  answers {
                    questions
                    groupCounts
                    results {
                      questionId
                      answers {
                        answerOption {
                          option
                          text
                        }
                        count
                      }
                      groupAnswers {
                        group
                        answers {
                          answerOption {
                            option
                            text
                          }
                          count
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  }
`)

export type BasicOutputsResult = NonNullable<
  NonNullable<
    BasicOutputGenerationPage_GenerateBasicOutputsStatusQuery['generateBasicOutputsStatus']
  >['result']
>

export type TabulationOutcome =
  | { kind: 'aborted' } // component unmounted; no user-facing message
  | { kind: 'emptyResult' } // arrived OK but empty/invalid payload
  | { kind: 'ok'; result: BasicOutputsResult }
  | { kind: 'serverError' } // status.error from the tabulation worker

type SubmitParams = {
  brandGroupId: number
  brandIds: number[] // explicit; may be empty when isEntireBrandGroupSelected is true
  customRoleAxes: CustomRoleAxes
  // Used to expand a customer-attribute-axis role's GROUP id into its attribute ids.
  customerAttributeGroups: {
    customerAttributes: { id: string }[]
    id: string
  }[]
  filterPatternDrafts: FilterPatternDraft[]
  isEntireBrandGroupSelected: boolean
  questionAssignments: QuestionAssignments
  questions: { mt_questions: string[]; question_id: string }[]
  surveyId: string
}

export const useGenerateBasicOutputs = () => {
  const isMounted = useMountedState()
  const graphQLRequest = useGraphQLRequest()
  const setAwaitId = useSetAtom(awaitIdAtom)
  const setPhase = useSetAtom(phaseAtom)

  // Phase 1: Send mutation, set phase to 'tabulating', return awaitId
  const [asyncState, startFn] = useAsyncFn(
    async ({
      brandGroupId,
      brandIds,
      customerAttributeGroups,
      customRoleAxes,
      filterPatternDrafts,
      isEntireBrandGroupSelected,
      questionAssignments,
      questions,
      surveyId,
    }: SubmitParams) => {
      // questionAssignments now carries builtin AND custom roles in one map, so a
      // single pass emits one selection per active role. A customer-attribute-axis
      // role holds a GROUP id, so it expands to that group's attribute ids instead of
      // going through the matrix sub-question expansion.
      const questionSelections = Object.entries(questionAssignments)
        .filter(([, qIds]) => 0 < (qIds?.length ?? 0))
        .map(([type, ids]) => {
          if (
            customRoleAxes[type as keyof CustomRoleAxes] === 'customerAttribute'
          ) {
            return {
              axis: 'CustomerAttribute' as const,
              customerAttributeIds: expandCustomerAttributeGroupId(
                customerAttributeGroups,
                ids[0],
                type,
              ),
              questionIds: [],
              type,
            }
          }

          const expandedIds = ids.flatMap((qId) => {
            const question = questions.find((q) => q.question_id === qId)
            if (question && 0 < question.mt_questions.length) {
              return question.mt_questions
            }

            return [qId]
          })

          return { axis: 'Question' as const, questionIds: expandedIds, type }
        })

      const filterPatterns = filterPatternDrafts.map((draft) => ({
        filter: { filter: draft.filterGroups },
        name: draft.name,
      }))

      const mutationResult = await graphQLRequest({
        document: generateBasicOutputsMutationDocument,
        variables: {
          brandGroupId,
          brandIds,
          filterPatterns,
          isEntireBrandGroupSelected,
          questionSelections,
          surveyId,
        },
      })

      const awaitId = mutationResult.generateBasicOutputs?.awaitId
      // Mirrors the tabulation flow (useTabulationQuery): a missing awaitId is a
      // failed submit, not a no-op. Throw so the caller's error path surfaces it
      // instead of silently resolving null (no error, no phase transition).
      if (!exists(awaitId)) {
        throw new Error(
          'basic-output: generateBasicOutputs returned no awaitId',
        )
      }

      setAwaitId(awaitId)
      setPhase('tabulating')

      return awaitId
    },
    [graphQLRequest, setAwaitId, setPhase],
  )

  // Phase 2: Poll for completion (called separately after phase becomes 'tabulating')
  const pollForResult = React.useCallback(
    async (awaitId: string): Promise<TabulationOutcome> =>
      new Promise<TabulationOutcome>((resolve) => {
        const pollUntilArrived = async () => {
          try {
            if (!isMounted()) {
              resolve({ kind: 'aborted' })

              return
            }

            const queryResult = await graphQLRequest({
              document: generateBasicOutputsStatusQueryDocument,
              variables: { awaitId },
            })

            const status = queryResult.generateBasicOutputsStatus

            if (!status?.arrived) {
              setTimeout(pollUntilArrived, 2000)

              return
            }

            if (exists(status.error)) {
              // Clear the in-flight marker so a failed run does not look
              // permanently in-flight and trigger stale-status restores later.
              setAwaitId(null)
              setPhase('input')
              resolve({ kind: 'serverError' })

              return
            }

            // Do NOT setPhase('rendering') here: the flow must write
            // resultRef.current BEFORE the phase becomes 'rendering', otherwise
            // the rendering effect (gated on phase==='rendering' && exists(ref))
            // can fire with a null ref, early-return, and never re-fire. The
            // caller transitions to 'rendering' in its .then after setting the
            // ref (see useBasicOutputGenerationFlow).
            // The tabulation request has terminated; clear the in-flight marker
            // so a completed run is not mistaken for one still running.
            setAwaitId(null)
            const result = status.result ?? null
            if (!exists(result)) {
              // Arrived with no error AND no result: an empty/invalid worker
              // payload. The caller resets phase to 'input' on emptyResult, so we
              // are not stuck, but log so the anomaly is not silently invisible.
              console.error(
                'basic-output: tabulation arrived without a result',
                status,
              )
              resolve({ kind: 'emptyResult' })

              return
            }
            resolve({ kind: 'ok', result })
          } catch (error) {
            console.error('Polling for basic outputs failed:', error)
            setAwaitId(null)
            setPhase('input')
            resolve({ kind: 'serverError' })
          }
        }

        pollUntilArrived()
      }),
    [graphQLRequest, isMounted, setAwaitId, setPhase],
  )

  return [asyncState, startFn, pollForResult] as const
}
